import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../AuthModule';
import { useT } from '../../i18n/I18nModule';
import { formatBotSenderDisplayName } from '../../i18n/botSender';

const API = process.env.REACT_APP_API_URL || '';

const GAME_SUGGESTION_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Replaced client-side with a real &lt;a&gt; so the SPA link always works (plain URLs in DMs were not always linkified). */
const PULINA_SUBMIT_DAILY_TOKEN = '[[PULINA_SUBMIT_DAILY]]';

function isRootGameSuggestionHttpUrl(urlString) {
  try {
    const u = new URL(urlString);
    const p = u.pathname.replace(/\/+$/, '') || '/';
    if (p !== '/') return false;
    const gs = u.searchParams.get('gameSuggestion')?.trim();
    return !!(gs && GAME_SUGGESTION_UUID_RE.test(gs));
  } catch {
    return false;
  }
}

function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Only same-app relative paths (e.g. /mail/123?rate-event=…); blocks javascript:, //, etc. */
function isSafeDmAnchorHref(href) {
  const h = String(href ?? '').trim();
  if (!h.startsWith('/') || h.startsWith('//')) return false;
  const head = h.split('?')[0];
  if (/^[a-zA-Z][a-zA-Z+.-]*:/.test(head)) return false;
  return true;
}

/**
 * DM bodies from the server may include <a href="/…">label</a> (e.g. event feedback).
 * linkifyDmPlainSegment only handles http(s) URLs and would escape these tags — parse them here first.
 */
function linkifyDmWithTrustedAnchors(raw, gameSuggestionStayPath) {
  const s = String(raw ?? '');
  if (!s) return '';
  const ANCHOR_RE = /<a\s+href="([^"]*)"\s*>([^<]*)<\/a>/gi;
  const parts = [];
  let last = 0;
  let m;
  while ((m = ANCHOR_RE.exec(s)) !== null) {
    if (m.index > last) {
      parts.push({ type: 'text', value: s.slice(last, m.index) });
    }
    parts.push({ type: 'a', href: m[1], label: m[2] });
    last = m.index + m[0].length;
  }
  if (last < s.length) {
    parts.push({ type: 'text', value: s.slice(last) });
  }
  return parts
    .map((p) => {
      if (p.type === 'text') {
        return linkifyDmPlainSegment(p.value, gameSuggestionStayPath);
      }
      if (!isSafeDmAnchorHref(p.href)) {
        return escapeHtml(`<a href="${p.href}">${p.label}</a>`);
      }
      const hrefEsc = escapeHtml(p.href);
      const labelEsc = escapeHtml(p.label);
      return `<a href="${hrefEsc}" target="_self" rel="noopener noreferrer">${labelEsc}</a>`;
    })
    .join('');
}

/** Turn http(s) URLs into links; escape everything else (safe for dangerouslySetInnerHTML). */
function linkifyDmPlainSegment(raw, gameSuggestionStayPath) {
  let s = String(raw ?? '');
  if (!s) return '';
  // Legacy DMs: host + path without scheme (client only linkifies https?://…)
  s = s.replace(
    /(^|[\s>])([a-z0-9][a-z0-9.-]*\.[a-z]{2,}\/submitDaily\/?)(?=[\s<.,;:!?]|$)/gi,
    (m, before, hostPath) => `${before}https://${hostPath}`,
  );
  const re = /https?:\/\/[^\s<>"']+/gi;
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(s)) !== null) {
    out.push(escapeHtml(s.slice(last, m.index)));
    const full = m[0];
    const trimmed = full.replace(/[),.;]+$/g, '');
    let href = trimmed;
    let target = '_blank';
    if (
      gameSuggestionStayPath &&
      isRootGameSuggestionHttpUrl(trimmed)
    ) {
      try {
        const u = new URL(trimmed);
        const id = u.searchParams.get('gameSuggestion')?.trim();
        if (id && GAME_SUGGESTION_UUID_RE.test(id)) {
          href = `${gameSuggestionStayPath}?gameSuggestion=${encodeURIComponent(id)}`;
          target = '_self';
        }
      } catch (_) {
        /* keep href as trimmed */
      }
    }
    try {
      const u = new URL(trimmed);
      const path = (u.pathname || '').replace(/\/+$/, '') || '/';
      if (path === '/submitDaily') {
        href = '/submitDaily';
        target = '_self';
      }
      if (path === '/eventPromo' || path === '/eventPromo/') {
        href = u.pathname + (u.search || '');
        target = '_self';
      }
      if (path === '/postPromo' || path === '/postPromo/') {
        href = u.pathname + (u.search || '');
        target = '_self';
      }
    } catch (_) {
      /* keep href as trimmed */
    }
    const hrefEsc = escapeHtml(href);
    const labelEsc = escapeHtml(trimmed);
    out.push(
      `<a href="${hrefEsc}" target="${target}" rel="noopener noreferrer">${labelEsc}</a>`,
    );
    if (trimmed.length < full.length) {
      out.push(escapeHtml(full.slice(trimmed.length)));
    }
    last = m.index + full.length;
  }
  out.push(escapeHtml(s.slice(last)));
  return out.join('').replace(/\n/g, '<br />');
}

function formatDmBodyHtml(raw, gameSuggestionStayPath) {
  const rawStr = String(raw ?? '');
  if (!rawStr) return '';
  if (rawStr.includes(PULINA_SUBMIT_DAILY_TOKEN)) {
    const cta = `<a class="PersonalMailView-dmCtaLink" href="/submitDaily" target="_self" rel="noopener noreferrer">Lisää päivän kysymys — avaa lomake</a>`;
    return rawStr
      .split(PULINA_SUBMIT_DAILY_TOKEN)
      .map((chunk) => linkifyDmWithTrustedAnchors(chunk, gameSuggestionStayPath))
      .join(cta);
  }
  return linkifyDmWithTrustedAnchors(rawStr, gameSuggestionStayPath);
}

export default function PersonalMailView({ messageId, eventRatingEventId }) {
  const { user } = useAuth();
  const t = useT();
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [replyError, setReplyError] = useState(null);
  const [ratingEvent, setRatingEvent] = useState(null);
  const [existingRating, setExistingRating] = useState(null);
  const [existingRatingLoading, setExistingRatingLoading] = useState(false);
  const [ratingModalDismissed, setRatingModalDismissed] = useState(false);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingReason, setRatingReason] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [ratingError, setRatingError] = useState(null);

  const loadMessage = useCallback(() => {
    if (!user?.id || !messageId) {
      setLoading(false);
      setError('Viestiä ei voitu ladata.');
      return;
    }
    const userIdStr = String(user.id).replace(/n$/, '');
    setLoading(true);
    setError(null);
    fetch(`${API}/api/direct-messages/open`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messageId, userId: userIdStr }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        setMessage(data || null);
      })
      .catch(() => {
        setError('Viestin lataus epäonnistui.');
      })
      .finally(() => setLoading(false));
  }, [user?.id, messageId]);

  useEffect(() => {
    loadMessage();
  }, [loadMessage]);

  // Reset dismissed when navigating to a different mail with event_rating
  useEffect(() => {
    if (eventRatingEventId) setRatingModalDismissed(false);
  }, [eventRatingEventId]);

  // When event_rating param is set, fetch that event; if it exists, we'll show the rating modal
  useEffect(() => {
    if (!eventRatingEventId || ratingModalDismissed) {
      setRatingEvent(null);
      return;
    }
    let cancelled = false;
    fetch(`${API}/api/events/${encodeURIComponent(eventRatingEventId)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (!cancelled) setRatingEvent(data || null);
      })
      .catch(() => {
        if (!cancelled) {
          setRatingEvent(null);
          setRatingModalDismissed(true);
          const u = new URL(window.location.href);
          u.searchParams.delete('rate-event');
          window.history.replaceState({}, '', u.pathname + u.search);
        }
      });
    return () => { cancelled = true; };
  }, [eventRatingEventId, ratingModalDismissed]);

  // When event is loaded, check if this user has already rated it
  useEffect(() => {
    if (!ratingEvent?.id || !user?.id) {
      setExistingRating(null);
      return;
    }
    const authorIdNorm = String(user.id).replace(/n$/, '');
    setExistingRatingLoading(true);
    setExistingRating(null);
    fetch(`${API}/api/events/${encodeURIComponent(ratingEvent.id)}/ratings`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((list) => {
        const mine = Array.isArray(list)
          ? list.find((r) => r.authorId != null && String(r.authorId) === authorIdNorm)
          : null;
        setExistingRating(mine ? { rating: mine.rating, reason: mine.reason ?? null } : null);
      })
      .catch(() => setExistingRating(null))
      .finally(() => setExistingRatingLoading(false));
  }, [ratingEvent?.id, user?.id]);

  const clearEventRatingParam = () => {
    const u = new URL(window.location.href);
    u.searchParams.delete('rate-event');
    window.history.replaceState({}, '', u.pathname + u.search);
  };

  const handleRatingCancel = () => {
    setRatingModalDismissed(true);
    setRatingEvent(null);
    clearEventRatingParam();
  };

  const handleSendReply = async () => {
    if (!replyDraft.trim() || !user?.id || !message) return;
    const threadPostId =
      message.id != null
        ? String(message.id).replace(/n$/, '')
        : String(messageId || '').replace(/n$/, '');
    if (!threadPostId) return;
    const userIdStr = String(user.id).replace(/n$/, '');
    setReplySubmitting(true);
    setReplyError(null);
    try {
      const res = await fetch(
        `${API}/api/direct-messages/${encodeURIComponent(threadPostId)}/replies`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: userIdStr, content: replyDraft.trim() }),
        },
      );
      if (!res.ok) throw new Error('reply failed');
      setReplyDraft('');
      loadMessage();
    } catch {
      setReplyError('Vastauksen lähetys epäonnistui.');
    } finally {
      setReplySubmitting(false);
    }
  };

  const handleRatingSubmit = async () => {
    if (!ratingEvent?.id || !user?.id) return;
    setSubmittingRating(true);
    setRatingError(null);
    try {
      await fetch(`${API}/api/events/${encodeURIComponent(ratingEvent.id)}/ratings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating: ratingValue,
          reason: ratingReason,
          userId: user.id,
        }),
      });
      setRatingModalDismissed(true);
      setRatingEvent(null);
      clearEventRatingParam();
      window.location.href = '/postilaatikko';
    } catch (err) {
      setRatingError('Arvion tallennus epäonnistui.');
    } finally {
      setSubmittingRating(false);
    }
  };

  if (!user) {
    return (
      <div className="App-block">
        <p>Kirjaudu sisään nähdäksesi viestit.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="App-block">
        <p>Ladataan viestiä…</p>
      </div>
    );
  }

  if (error || !message) {
    return (
      <div className="App-block">
        <p>{error || 'Viestiä ei löytynyt.'}</p>
        <p>
          <a href="/asetukset">← Takaisin asetuksiin</a>
        </p>
      </div>
    );
  }

  // When rate-event is set: show only the rating form or loading or "already rated" (replace mail body)
  if (eventRatingEventId && !ratingModalDismissed) {
    if (!ratingEvent) {
      return (
        <div className="App-block">
          <p>Ladataan tapahtumaa…</p>
        </div>
      );
    }
    const isOwner = ratingEvent?.createdByUserId != null && user?.id != null && String(ratingEvent.createdByUserId) === String(user.id);
    if (isOwner) {
      return (
        <div className="App-block">
          <div className="EventRatingPrompt">
            <div className="EventRatingPrompt-card">
              <p>Et voi arvioida omaa tapahtumaasi.</p>
              <div className="EventRatingPrompt-actions">
                <button type="button" className="Button Button-secondary" onClick={handleRatingCancel}>
                  Sulje
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    if (existingRatingLoading) {
      return (
        <div className="App-block">
          <p>Ladataan…</p>
        </div>
      );
    }
    if (existingRating != null) {
      const title = ratingEvent.title || '—';
      const rating = existingRating.rating;
      const reason = existingRating.reason;
      const reasonSuffix = reason ? ` kommentilla "${reason}"` : '';
      return (
        <div className="App-block">
          <div className="EventRatingPrompt">
            <div className="EventRatingPrompt-card">
              <p>
                Arvostelit jo tapahtuman <strong>{title}</strong> {rating} / 5{reasonSuffix && <> {reasonSuffix}</>}.
              </p>
              <div className="EventRatingPrompt-actions">
                <button
                  type="button"
                  className="Button Button-secondary"
                  onClick={handleRatingCancel}
                >
                  Sulje
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="App-block">
        <div className="EventRatingPrompt">
          <div className="EventRatingPrompt-card">
            <p>
              Kiitos kun osallistuit tapahtumaan{' '}
              <strong>{ratingEvent.title || '—'}</strong>, jonka järjesti{' '}
              <strong>{ratingEvent.author || '—'}</strong>.
            </p>
            <div className="EventRatingPrompt-stars">
              {[0, 1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={n === ratingValue ? 'Star Star--active' : 'Star'}
                  onClick={() => setRatingValue(n)}
                  aria-label={`${n} tähteä`}
                >
                  {n}★
                </button>
              ))}
            </div>
            <label className="EventRatingPrompt-label">
              Kerro halutessasi, miksi annoit tämän arvion (valinnainen)
              <textarea
                value={ratingReason}
                onChange={(e) => setRatingReason(e.target.value)}
                rows={3}
              />
            </label>
            {ratingError && <p className="EventRatingPrompt-error">{ratingError}</p>}
            <div className="EventRatingPrompt-actions">
              <button
                type="button"
                className="Button Button-secondary"
                onClick={handleRatingCancel}
              >
                Sulje
              </button>
              <button
                type="button"
                className="Button"
                disabled={submittingRating}
                onClick={handleRatingSubmit}
              >
                Lähetä arvio
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const fromName = formatBotSenderDisplayName(
    {
      senderKind: message.fromUser?.senderKind,
      fromUserId: message.fromUser?.id,
      fromDisplayName: message.fromUser?.displayName,
      fromUsername: message.fromUser?.username,
    },
    t,
  );
  const toName =
    message.toUser?.displayName ||
    message.toUser?.username ||
    user.displayName ||
    user.username;
  const created =
    message.createdAt != null
      ? new Date(message.createdAt).toLocaleString('fi-FI')
      : '';

  const replies = Array.isArray(message.replies) ? message.replies : [];
  const canReply = message.canReply === true;
  const currentUserIdStr = user?.id != null ? String(user.id).replace(/n$/, '') : '';

  const handleBackOrCancelReply = () => {
    if (canReply && replyDraft.trim()) {
      setReplyDraft('');
      setReplyError(null);
      return;
    }
    window.location.href = '/postilaatikko';
  };

  const backActionLabel =
    canReply && replyDraft.trim() ? 'Peruuta' : 'Takaisin';

  return (
    <div className="App-block">
      <div className="Profile-header-wrap">
        <div className="Profile-heading-row">
          <h2 className="Profile-heading">
            Yksityisviesti
          </h2>
        </div>
      </div>
      <div className="PersonalMailView">
        <div className="PersonalMailView-meta-row">
          <span className="PersonalMailView-label">Keneltä:</span>
          <span>{fromName}</span>
        </div>
        <div className="PersonalMailView-meta-row">
          <span className="PersonalMailView-label">Kenelle:</span>
          <span>{toName}</span>
        </div>
        <div className="PersonalMailView-meta-row">
          <span className="PersonalMailView-label">Aika:</span>
          <span>{created}</span>
        </div>
        <div className="PersonalMailView-message">
          <p
            dangerouslySetInnerHTML={{
              __html: formatDmBodyHtml(
              message.content,
              `/mail/${String(message.id).replace(/n$/, '')}`,
            ),
            }}
          />
        </div>
        {replies.length > 0 && (
          <div className="PersonalMailView-thread" aria-label="Vastaukset">
            <h3 className="PersonalMailView-thread-title">Ketju</h3>
            <ul className="PersonalMailView-thread-list">
              {replies.map((r) => {
                const who =
                  r.author?.displayName || r.author?.username || '—';
                const when =
                  r.createdAt != null
                    ? new Date(r.createdAt).toLocaleString('fi-FI')
                    : '';
                const mine =
                  r.authorId != null &&
                  String(r.authorId) === currentUserIdStr;
                return (
                  <li
                    key={String(r.id)}
                    className={`PersonalMailView-thread-item${mine ? ' PersonalMailView-thread-item--mine' : ''}`}
                  >
                    <div className="PersonalMailView-thread-meta">
                      <span className="PersonalMailView-thread-author">{who}</span>
                      <span className="PersonalMailView-thread-time">{when}</span>
                    </div>
                    <div className="PersonalMailView-thread-body">{r.postContent}</div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {canReply && (
          <div className="PersonalMailView-reply">
            <label className="PersonalMailView-reply-label" htmlFor="dm-reply-input">
              Vastaa
            </label>
            <textarea
              id="dm-reply-input"
              className="PersonalMailView-reply-input"
              rows={4}
              maxLength={2000}
              value={replyDraft}
              onChange={(e) => setReplyDraft(e.target.value)}
              placeholder="Kirjoita vastaus…"
              disabled={replySubmitting}
            />
            {replyError && (
              <p className="PersonalMailView-reply-error">{replyError}</p>
            )}
          </div>
        )}
        <div className="PersonalMailView-actions">
          <p className="PersonalMailView-back">
            <button
              type="button"
              className="PersonalMailView-back-action"
              onClick={handleBackOrCancelReply}
            >
              {backActionLabel}
            </button>
          </p>
          {canReply ? (
            <button
              type="button"
              className="Button PersonalMailView-reply-send"
              disabled={replySubmitting || !replyDraft.trim()}
              onClick={handleSendReply}
            >
              Lähetä vastaus
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

